import { useCallback, useMemo, useState } from "react";
import { LotsTableRowChange, LotsTableRowErrors } from "../lots.types";
import {
  validateLotGoalWeight,
  LotGoalWeightError,
  validateLotMeasuredWeight,
  LotMeasuredWeightError,
} from "../utils/lots.validators";
import { useTranslations } from "adapters/i18n";
import { DateUtils } from "shared/dateUtils";

export function useLotTableForm() {
  const { t } = useTranslations();

  const [tableChanges, setTableChanges] = useState<LotsTableRowChange>({});
  const [tableChangeErrors, setTableChangeErrors] =
    useState<LotsTableRowErrors>({});

  const goalWeightErrorMessageMap = useMemo<Record<LotGoalWeightError, string>>(
    () => ({
      [LotGoalWeightError.NonZero]: t("livestock:zero-weight-error"),
    }),
    [t]
  );

  const measuredWeightErrorMessageMap = useMemo<
    Record<LotMeasuredWeightError, string>
  >(
    () => ({
      [LotMeasuredWeightError.NonZero]: t("livestock:zero-weight-error"),
    }),
    [t]
  );

  const validateField = useCallback(
    <T extends keyof LotsTableRowChange[number]>(
      field: T,
      value: LotsTableRowChange[number][T]
    ): string | null => {
      if (field === "goalWeight") {
        const error = validateLotGoalWeight(value as number | null);
        const message = error != null ? goalWeightErrorMessageMap[error] : null;
        return message;
      }

      if (field === "measuredWeight") {
        const error = validateLotMeasuredWeight(value as number | null);
        const message =
          error != null ? measuredWeightErrorMessageMap[error] : null;
        return message;
      }

      return null;
    },
    [goalWeightErrorMessageMap, measuredWeightErrorMessageMap]
  );

  const handleRowFieldChange = <T extends keyof LotsTableRowChange[number]>(
    lotId: number,
    field: T,
    value: LotsTableRowChange[number][T]
  ) => {
    setTableChanges((prev) => ({
      ...prev,
      [lotId]: { ...prev[lotId], [field]: value },
    }));
    setTableChangeErrors((prev) => ({
      ...prev,
      [lotId]: { ...prev[lotId], [field]: null },
    }));
  };

  const handleRowFieldError = <T extends keyof LotsTableRowErrors[number]>(
    lotId: number,
    field: T,
    error: string | null
  ) => {
    setTableChangeErrors((prev) => ({
      ...prev,
      [lotId]: { ...prev[lotId], [field]: error },
    }));
  };

  const handleBlurRowField = useCallback(
    <T extends keyof LotsTableRowErrors[number]>(lotId: number, field: T) => {
      const value = tableChanges[lotId]?.[field];
      const error = validateField(field, value);
      setTableChangeErrors((prev) => ({
        ...prev,
        [lotId]: { ...prev[lotId], [field]: error },
      }));
    },
    [tableChanges, validateField]
  );

  const canSubmitForm = useMemo(() => {
    const hasErrors = Object.values(tableChangeErrors).some((errors) =>
      Object.values(errors).some((error) => error != null)
    );

    const hasAnyDateError = Object.values(tableChanges).some(
      (rowChange) =>
        rowChange.weighingDate != null &&
        !DateUtils.parse(rowChange.weighingDate).isValid()
    );

    return !hasErrors && !hasAnyDateError;
  }, [tableChangeErrors, tableChanges]);

  return {
    canSubmitForm,
    handleBlurRowField,
    handleRowFieldChange,
    handleRowFieldError,
    tableChangeErrors,
    tableChanges,
  };
}
